/* eslint-disable react-hooks/exhaustive-deps */
// wijmo
import * as wjCore from "@grapecity/wijmo"
import * as wjGrid from "@grapecity/wijmo.grid"
import * as wjGridFilter from "@grapecity/wijmo.grid.filter"
import * as wjrGrid from "@grapecity/wijmo.react.grid"
import * as wjRGridFilter from "@grapecity/wijmo.react.grid.filter"
import * as wjcGridPdf from "@grapecity/wijmo.grid.pdf"
import * as wjcGridXlsx from "@grapecity/wijmo.grid.xlsx"
import React, {
  CSSProperties,
  MouseEventHandler,
  useEffect,
  useState,
} from "react"
import { Button, Row, Col } from "react-bootstrap"
import { useLocation } from "react-router-dom"
import { GridRowPickupButton, setDefaultLayout } from "./GridRowPickupButton"
import { GRID_PROPERTY_SET_TYPE } from "../../constant/flexGridConstant"
import * as gridPageHelper from "../../ts/helpers/gridPageHelper"
import GridFooter from "./GridFooter"
import { LOCALSTORAGE_PROPERTIES } from "../../constant/localstorageProperties"
import util from "../../ts/util"

type TGridEventHandlers = {
  gridInitializedHandler?: (grid: wjGrid.FlexGrid) => void
  gridItemsSourceChangedHandler?: (grid: wjGrid.FlexGrid) => void
  formatItem?: (f: wjGrid.FlexGrid, e: wjGrid.FormatItemEventArgs) => void
}

type TGridFooter = {
  pdfFileName?: string
  excelFileName?: string
  isEnableExport?: boolean
  pdfFormatItem?: (args: wjcGridPdf.PdfFormatItemEventArgs) => void
  excelFormatItem?: (args: wjcGridXlsx.XlsxFormatItemEventArgs) => void
  isEnablePageSize?: "visible" | "hidden" | undefined
  isHiddenGridDataCount?: boolean
  excelIncludeColumns?: string[]
  changePageSizeTargetNumberArray?: number[]
}

type TFlexGridOuter = {
  gridId?: string
  flexGrid: wjGrid.FlexGrid | undefined
  setFlexGrid: (
    value: React.SetStateAction<wjGrid.FlexGrid | undefined>
  ) => void
  gridData: { data: wjCore.CollectionView }
  setGridData: React.Dispatch<
    React.SetStateAction<{
      data: wjCore.CollectionView
    }>
  >
  gridInitialized: boolean
  setGridInitialized: React.Dispatch<React.SetStateAction<boolean>>
  isRestoreSearching: boolean
  setIsRestoreSearching: React.Dispatch<React.SetStateAction<boolean>>
  searchConditionClear?: MouseEventHandler<HTMLElement>
  pageNumber: number
  style?: CSSProperties
  pageSize: number
  setPageSize: React.Dispatch<React.SetStateAction<number>>
  headerComponent?: React.ReactNode
  headerComponentClassName?: string
  isShowGridHeader?: boolean
  isShowGridFooter?: boolean
  isShowGridRowPickupButton?: boolean
  /**
   * レイアウト（列表示設定）適用後に呼び出されるコールバック
   * 検索条件によって表示可否が変わる列など、
   * レイアウトへ保存させたくない列の状態を画面側で再適用するために使用する
   */
  afterApplyGridLayout?: (grid: wjGrid.FlexGrid) => void
  hasFilter?: boolean
  ValueFilterCustomizedFunc?: (filter: wjGridFilter.FlexGridFilter) => unknown
  filterColumns?: string[]
  gridConstantProperties?: GRID_PROPERTY_SET_TYPE
  gridProperties?: wjrGrid.FlexGrid["props"]
} & TGridEventHandlers &
  TGridFooter

const FlexGridOuter: React.FC<TFlexGridOuter> = (props) => {
  const gridId = props.gridId ?? "flexGrid1"
  const location = useLocation()

  // MEMO:location.pathnameに /syain/111 のようにIDが含まれているとIDごとにレイアウトを保存してしまうため、ID部分を*にマスクし、gridLayoutのKeyとする
  const gridLayoutPath = location?.pathname
    .split("/")
    .map((v) => (v?.match("^[0-9]+$") ? "*" : v))
    .join("/")
  const gridLayoutKey = `${LOCALSTORAGE_PROPERTIES.PAGE_CONDITION_DATA}_${gridLayoutPath}_${gridId}`
  const gridPageSizeKey = `${LOCALSTORAGE_PROPERTIES.GRID_PAGE_SIZE}_${gridLayoutPath}_${gridId}`

  //#region [state]

  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(0)

  const [flexGridFilter, setFlexGridFilter] =
    useState<wjGridFilter.FlexGridFilter>()

  const [filterEdited, setFilterEdited] = useState(false)

  // リストア制御用フラグ

  const [isFirstSearchCompleted, setIsFirstSearchCompleted] =
    useState<boolean>(false)

  // グリッド初期化フラグ
  const [flexGridInitialized, setFlexGridInitialized] = useState<boolean>(false)
  const [flexGridFilterInitialized, setFlexGridFilterInitialized] =
    useState<boolean>(false)

  const [gridDefaultLayout, setGridDefaultLayout] = useState<string>("")

  // フィルター制御完了フラグ
  const [フィルターセット完了フラグ, setフィルターセット完了フラグ] =
    useState(false)
  //#endregion

  // グリッド初期化時イベントハンドラ
  const gridInitializedHandler = async (grid: wjGrid.FlexGrid) => {
    await setDefaultLayout(
      grid,
      setGridDefaultLayout,
      gridLayoutKey,
      props.afterApplyGridLayout
    )
    props.gridInitializedHandler && props.gridInitializedHandler(grid)

    // IMEモードのフォーカス対策にメソッド追加
    // 下記参照
    // https://developer-tools.zendesk.com/hc/ja/articles/5262007104911--FlexGrid-imeEnabled-true%E3%81%A7%E8%A1%A8%E7%A4%BA%E7%AF%84%E5%9B%B2%E5%A4%96%E3%81%AE%E3%82%BB%E3%83%AB%E3%82%92%E9%81%B8%E6%8A%9E%E3%81%97%E3%81%A6%E3%81%84%E3%82%8B%E5%A0%B4%E5%90%88%E3%81%ABTab%E3%82%AD%E3%83%BC%E3%82%92%E6%8A%BC%E3%81%97%E3%81%A6%E3%82%82%E6%AC%A1%E3%81%AE%E3%82%B3%E3%83%B3%E3%83%88%E3%83%AD%E3%83%BC%E3%83%AB%E3%81%AB%E3%83%95%E3%82%A9%E3%83%BC%E3%82%AB%E3%82%B9%E3%81%8C%E7%A7%BB%E5%8B%95%E3%81%97%E3%81%AA%E3%81%84
    const updateFocus = () => {
      let _host = grid.hostElement
      let _tbx =
        _host.querySelector(
          'textarea[class="wj-grid-editor wj-form-control wj-grid-ime"]'
        ) ?? undefined
      let _eFocus = _host.querySelector('div[wj-part="focus"]')
      let _ae = wjCore.getActiveElement()
      if (
        !grid.activeEditor &&
        wjCore.closest(_ae, ".wj-flexgrid") === grid.hostElement
      ) {
        if (grid.imeEnabled && !grid._wantsInput(_ae)) {
          if (!grid.activeCell) {
            if (_tbx instanceof HTMLElement && _eFocus instanceof HTMLElement) {
              _tbx.tabIndex = -1
              _eFocus?.focus()
            }
          }
        }
      }
    }
    grid.hostElement.addEventListener(
      "keydown",
      (e) => {
        // Tabを押したときにフォーカスを移動するようにする
        if (e.key === "Tab") {
          updateFocus()
        }
      },
      true
    )

    // フィルターの存在しないときはここで初期flexgridを代入
    if (props.hasFilter === false) {
      props.setFlexGrid(grid)
    }
  }

  // グリッドフィルタ初期化時イベントハンドラ
  const gridFilterInitializedHandler = (e: wjGridFilter.FlexGridFilter) => {
    setFlexGridFilter(e)
    if (props.hasFilter !== false) {
      props.setFlexGrid(e.grid)
    }
  }

  // グリッドの選択変更時イベントハンドラ
  let t = undefined as unknown as NodeJS.Timeout
  const gridSelectionChangedHandler = (
    grid: wjGrid.FlexGrid,
    e: wjGrid.CellRangeEventArgs
  ) => {
    if (t) {
      clearTimeout(t)
    }
    // 選択行に列の色変更用のクラスを入れる
    grid.rows.forEach((row) => {
      if (row.index === e.row) {
        if (!row.cssClassAll?.includes("row-selected")) {
          row.cssClassAll = [
            ...(row.cssClassAll?.split(" ") ?? []),
            "row-selected",
          ].join(" ")
        }
      } else {
        if (row.cssClassAll) {
          row.cssClassAll = [
            ...(row.cssClassAll?.split(" ") ?? []).filter(
              (className) => className !== "row-selected"
            ),
          ].join(" ")
        }
      }
    })
    t = setTimeout(() => {
      setSelectedRowIndex(e.row)
    }, 500)
  }

  const editingFilterHandler = (
    gridFilter: wjGridFilter.FlexGridFilter,
    e: wjGrid.CellRangeEventArgs
  ) => {
    setFilterEdited(true)
  }

  // グリッドフィルタ変更時イベントハンドラ
  const gridFilterChangedHandler = (
    gridFilter: wjGridFilter.FlexGridFilter,
    e: wjGrid.CellRangeEventArgs
  ) => {
    saveGridFilterStateToLocalStorage(gridFilter)
    setフィルターセット完了フラグ(true)
  }

  // グリッドソート実施後イベントハンドラ
  const gridSortedHandler = (
    grid: wjGrid.FlexGrid,
    e: wjGrid.CellRangeEventArgs
  ) => {
    // ソート状態はマスクしたパスではなく、実パスで管理とする
    gridPageHelper.saveGridSortState(location.pathname, grid)
  }

  // グリッドItemSource変更完了時イベントハンドラ
  const gridItemsSourceChangedHandler = (grid: wjGrid.FlexGrid) => {
    const refreshedHandler = (g: any, _: any) => {
      // 呼び出された時点で自分自身のハンドラを解除
      g.refreshed.removeHandler(refreshedHandler)

      // 初回検索完了
      setIsFirstSearchCompleted(true)
    }
    // ItemSourceに変更後のrefreshedを用いることで、描画完了時のイベントを補足
    grid.refreshed.addHandler(refreshedHandler)

    props.gridItemsSourceChangedHandler &&
      props.gridItemsSourceChangedHandler(grid)
  }

  //#region [baseMethod]

  /**
   * グリッドフィルタの状態をローカルストレージに保存
   * @param gridFilter グリッドフィルタ
   */
  const saveGridFilterStateToLocalStorage = (
    gridFilter: wjGridFilter.FlexGridFilter
  ) => {
    // フィルタ状態はマスクしたパスではなく、実パスで管理とする
    gridPageHelper.saveGridFilterAndSortState(location.pathname, gridFilter)
  }

  //#endregion

  //#region [effect]

  useEffect(() => {
    // ページサイズを復元
    gridPageHelper.restorePageSize(gridPageSizeKey, props.setPageSize)
    // setTimeoutのcleanup
    return () => {
      setFlexGridFilter(undefined)
      if (t) {
        clearTimeout(t)
      }
    }
  }, [])

  /**
   * grid初期化完了フラグON
   */
  useEffect(() => {
    if (!flexGridInitialized && props.flexGrid != null)
      setFlexGridInitialized(true)
  }, [flexGridInitialized, props.flexGrid])

  /**
   * gridFilter初期化完了フラグON
   */
  useEffect(() => {
    if (!flexGridFilterInitialized && flexGridFilter != null)
      setFlexGridFilterInitialized(true)
  }, [flexGridFilterInitialized, flexGridFilter])

  useEffect(() => {
    if (flexGridInitialized && flexGridFilterInitialized) {
      props.setGridInitialized(true)
    }
  }, [flexGridInitialized, flexGridFilterInitialized])
  useEffect(() => {
    // リストア検索中 かつ 読み込み完了時
    if (props.isRestoreSearching && isFirstSearchCompleted) {
      if (props.flexGrid != null && flexGridFilter != null) {
        // グリッドのフィルタ条件を復元
        // フィルタ状態はマスクしたパスではなく、実パスで管理とする
        gridPageHelper
          .restoreGridFilterAndSortState(
            location.pathname,
            flexGridFilter,
            props.flexGrid.hostElement.id
          )
          .then(() => {
            // 復元が完了したらページ条件を復元
            gridPageHelper.restorePageConditionData(location.pathname)

            // 復元が終わったので保存していた設定を削除
            gridPageHelper.removePageConditionData()
            gridPageHelper.removeAllGridFilterAndSortStates()

            // リストア処理完了とするためフラグOFF
            props.setIsRestoreSearching(false)
          })
      }
    }
  }, [props.isRestoreSearching, isFirstSearchCompleted])

  /**
   * グリッドのフィルタ状態を保存
   */
  useEffect(() => {
    // リストア中でないこと
    // 初期検索が完了していること
    if (flexGridFilter && !props.isRestoreSearching && isFirstSearchCompleted) {
      saveGridFilterStateToLocalStorage(flexGridFilter)
    }
  }, [
    props.pageNumber,
    selectedRowIndex,
    flexGridFilter,
    props.isRestoreSearching,
    isFirstSearchCompleted,
  ])

  /**
   * ページサイズを保存
   */
  useEffect(() => {
    if (props.flexGrid != null) {
      gridPageHelper.savePageSize({
        ページサイズ: props.pageSize,
        pageSizeLayoutKey: gridPageSizeKey,
      })
    }
  }, [props.pageSize])

  useEffect(() => {
    if (!flexGridFilter || !props.flexGrid) return

    // 外部からカスタマイズをもらってる場合、そちらを適用する
    if (props.ValueFilterCustomizedFunc) {
      props.ValueFilterCustomizedFunc(flexGridFilter)
    } else {
      util.defaultValueFieldCustomizedFunc(flexGridFilter, props.flexGrid)
    }
  }, [props.gridData.data, props.flexGrid, flexGridFilter])

  // 表示件数確認用
  useEffect(() => {
    if (フィルターセット完了フラグ) {
      setフィルターセット完了フラグ(false)
    }
  }, [フィルターセット完了フラグ])

  // 250件以上表示時の警告追加
  useEffect(() => {
    if (!filterEdited || !flexGridFilter || !flexGridFilter.activeEditor) return
    const binding = flexGridFilter.activeEditor.filter.column.binding
    if (!binding) return

    const columnFilter = flexGridFilter.getColumnFilter(binding, true)
    const sourceCollection: any[] =
      flexGridFilter.grid.collectionView.sourceCollection
    const valueFilter = columnFilter.valueFilter

    const hostElement = flexGridFilter.activeEditor.hostElement
    const targetElement = hostElement.querySelector(
      'div[wj-part="div-edt-val"]'
    ) as HTMLElement

    if (!targetElement) return

    const newContent = ` 
      <div class="fw-bold flexgrid-filter-limit-message">
      <p>【※上位250項目のみ対象です】</p>
    </div>`

    const maxValue = valueFilter.maxValues

    const uniqueValues = sourceCollection
      .map((v) => v[binding])
      .filter((value, i, array) => array.indexOf(value) === i)

    if (uniqueValues.length > maxValue) {
      targetElement.insertAdjacentHTML("beforeend", newContent)
    } else {
      const existingElement = targetElement.querySelector(
        ".flexgrid-filter-limit-message"
      )
      if (existingElement) {
        existingElement.remove()
      }
    }
    setFilterEdited(false)
  }, [filterEdited])

  //#endregion
  return (
    <>
      <Row
        className="grid-header-row"
        style={{ display: props.isShowGridHeader !== false ? "flex" : "none" }}>
        <Col className={props.headerComponentClassName}>
          {props.headerComponent}
        </Col>
        <Col xs={{ span: "auto" }} className="text-end">
          {props.searchConditionClear && (
            <Button onClick={props.searchConditionClear} variant="secondary">
              検索条件をクリアする
            </Button>
          )}
          {props.isShowGridRowPickupButton !== false && (
            <GridRowPickupButton
              flexGrid={props.flexGrid}
              thisGridLayoutKey={gridLayoutKey}
              gridDefaultLayout={gridDefaultLayout}
              gridName={gridId}
              gridLayoutPath={gridLayoutPath}
              afterApplyGridLayout={props.afterApplyGridLayout}
              thisPageSizeLayoutKey={gridPageSizeKey}></GridRowPickupButton>
          )}
        </Col>
      </Row>
      <wjrGrid.FlexGrid
        id={gridId}
        {...props.gridConstantProperties}
        //classNameを置くことができないためstyleを直接操作する
        style={{
          maxHeight: "46rem",
          ...props.style,
        }}
        {...props.gridProperties}
        initialized={gridInitializedHandler}
        itemsSource={props.gridData.data}
        itemsSourceChanged={gridItemsSourceChangedHandler}
        selectionChanged={gridSelectionChangedHandler}
        sortedColumn={gridSortedHandler}
        formatItem={props.formatItem}
        imeEnabled={true}>
        <wjRGridFilter.FlexGridFilter
          filterColumns={props.filterColumns}
          showFilterIcons={props.hasFilter ?? true}
          initialized={gridFilterInitializedHandler}
          filterChanged={gridFilterChangedHandler}
          editingFilter={editingFilterHandler}
        />
        {props.children}
      </wjrGrid.FlexGrid>
      {/* footer */}
      {props.isShowGridFooter !== false && (
        <GridFooter
          excelFileName={props.excelFileName}
          isEnableExport={props.isEnableExport}
          isEnablePageSize={props.isEnablePageSize}
          pdfFileName={props.pdfFileName}
          excelFormatItem={props.excelFormatItem}
          pdfFormatItem={props.pdfFormatItem}
          pageSize={props.pageSize}
          setPageSize={props.setPageSize}
          gridData={props.gridData}
          flexGrid={props.flexGrid}
          excelIncludeColumns={props.excelIncludeColumns}
          changePageSizeTargetNumberArray={
            props.changePageSizeTargetNumberArray
          }>
          <>
            {!props.isHiddenGridDataCount && (
              <>
                全{props?.gridData?.data?.sourceCollection?.length}件中
                {props?.gridData?.data?.itemCount ?? 0}件表示
              </>
            )}
            {/* プリミティブな値の変更に対応するために変更を検知するためだけの強制リロード要素 */}
            <div style={{ display: "none" }}>{フィルターセット完了フラグ}</div>
          </>
        </GridFooter>
      )}
    </>
  )
}

export default FlexGridOuter
